import React, {useEffect, useState} from 'react';
import { PageTitle } from '../../_metronic/layout/core';
import { NavButtonGroup } from './index';
import { useDispatch, useSelector } from 'react-redux';
import { reduxProps } from '../../constants';
import { actionAddMultipleStudents } from '../../redux/education';
import { useNavigate } from 'react-router-dom';
import LoadingButton from '../../components/LoadingButton';
import {toast} from 'react-toastify';
import {store} from "../../redux/ROOT";
import {SET_TASK_COMPLETED} from "../../redux/onboarding/actions";
import * as fbq from "../../utils/FacebookPixel";

interface StudentFormData {
  name: string;
  lastName: string;
  email: string;
}

const MAX_STUDENTS = 20;

export function StudentAdd() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { classroom } = useSelector((state: reduxProps) => state.educationReducer);
  const [students, setStudents] = useState<StudentFormData[]>(
    Array(5).fill({ name: '', lastName: '', email: '' })
  );

  const handleAddStudent = () => {
    if (students.length >= MAX_STUDENTS) {
      toast.error('Maksimum öğrenci sayısına ulaştınız');
      return;
    }
    setStudents([...students, { name: '', lastName: '', email: '' }]);
  };

  const handleInputChange = (index: number, field: keyof StudentFormData, value: string) => {
    const updatedStudents = [...students];
    updatedStudents[index] = { ...updatedStudents[index], [field]: value };
    setStudents(updatedStudents);
  };

  const handleSubmit = async () => {
    try {
      // Sadece adı ve soyadı dolu olan öğrencileri filtrele
      const validStudents = students.filter(student =>
        student.name.trim() !== '' && student.lastName.trim() !== ''
      );

      if (validStudents.length === 0) {
        toast.error('En az bir öğrenci bilgisi girilmelidir.');
        throw new Error('En az bir öğrenci bilgisi girilmelidir.');
      }

      // Yarım kalmış öğrenci bilgisi var mı kontrol et
      const hasIncompleteStudent = students.some(student =>
        (student.name.trim() !== '' && student.lastName.trim() === '') ||
        (student.name.trim() === '' && student.lastName.trim() !== '')
      );

      if (hasIncompleteStudent) {
        toast.error('Lütfen başladığınız öğrenci bilgilerini tamamlayın.');
        throw new Error('Lütfen başladığınız öğrenci bilgilerini tamamlayın.');
      }

      const payload = {
        dataList: validStudents.map(student => ({
          name: student.name,
          lastName: student.lastName,
          email: student.email || undefined,
        })),
        organizationId: classroom.organizationId,
        schoolId: classroom.schoolId,
        classroomId: classroom.id,
        gradeLevelId: classroom.gradeLevelId
      };

      return new Promise<void>((resolve, reject) => {
        dispatch(
          actionAddMultipleStudents(payload, (response) => {
            if (response.type === 'SUCCESS') {
              console.log('Öğrenci ekleme işlemi başarılı', response.payload.data);
              store.dispatch({ type: SET_TASK_COMPLETED, payload: 'studentsAdded', value: true });
              fbq.event('StudentAdded', {
                content_name: 'Student Added',
                content_category: 'Onboarding',
              });
              window.lintrk?.('track', { conversion_id: 21977097 });
              toast.success(`Başarıyla eklendi`, {
                autoClose: 3000,
                position: 'top-right',
              });
              navigate('/student');
              resolve();
            } else {
              toast.error('Öğrenciler eklenirken bir hata oluştu', {
                autoClose: 3000,
                position: 'top-right',
              });
              reject(new Error('Öğrenciler eklenirken bir hata oluştu'));
            }
          })
        );
      });
    } catch (error) {
      console.error("Hata:", error);
      throw error;
    }
  };

  return (
    <div id='students'>
      <PageTitle description={'Tek seferde en fazla 20 öğrenci ekleyebilirsiniz.'}>
        Öğrenci Ekle
      </PageTitle>

      <NavButtonGroup/>

      <div className='card' id="studentBox">
        <div className='card-body py-3'>
          <div className='form-container'>
            <h2>Öğrenci Ekle</h2>
            <p>Öğrenci adını soyadını girerek giriş bilgilerini alabilirsiniz. Dilerseniz e-posta adreslerini ekleyerek bilgileri onlara gönderebilirsiniz.</p>

            <div className="formWrapper">
              <div className='student-form-header mb-4'>
                <div className='row'>
                  <div className='col-md-4'>
                    <label>Öğrenci İsim</label>
                  </div>
                  <div className='col-md-4'>
                    <label>Öğrenci Soy İsim</label>
                  </div>
                  <div className='col-md-4'>
                    <label>E-posta adresi (İsteğe bağlı)</label>
                  </div>
                </div>
              </div>

              {students.map((student, index) => (
                  <div key={index} className='student-form-group mb-4'>
                    <div className='row'>
                      <div className='col-md-4'>
                        <input
                            type='text'
                            className='form-control'
                            placeholder='Öğrenci İsim'
                            value={student.name}
                            onChange={(e) => handleInputChange(index, 'name', e.target.value)}
                        />
                      </div>
                      <div className='col-md-4'>
                        <input
                            type='text'
                            className='form-control'
                            placeholder='Öğrenci Soy İsim'
                            value={student.lastName}
                            onChange={(e) => handleInputChange(index, 'lastName', e.target.value)}
                        />
                      </div>
                      <div className='col-md-4'>
                        <input
                            type='email'
                            className='form-control'
                            placeholder='Öğrenci e-posta adresi'
                            value={student.email}
                            onChange={(e) => handleInputChange(index, 'email', e.target.value)}
                        />
                      </div>
                    </div>
                  </div>
              ))}

              <button
                  type='button'
                  onClick={handleAddStudent}
                  className='btn btn-light'
                  aria-label='Başka öğrenci satırı ekle'
                  disabled={students.length >= MAX_STUDENTS}
              >
                <i className="fas fa-plus" aria-hidden='true'></i> Öğrenci satırı ekle
              </button>

              <div className='d-flex justify-content-center gap-4 mt-8'>
                <LoadingButton onClick={handleSubmit}>
                  Öğrencileri Kaydet
                </LoadingButton>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
